import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICompany, ITag, ICompanySettings } from '../../../common';
import {
    IFetchCompanyInfoAction,
    IFetchCompanySettingsAction,
    IPostConnectGmailAction,
    IPostConnectOutlookAction,
    ISetCompanyInfoAction,
    ISetTagsAction,
    IUpdateTagAction,
    ICreateTagAction,
    IDeleteTagAction,
    IDeleteCompanyMailboxAction,
    ISetCompanySettingsAction,
    IUpdateCompanySettingsAction,
    ISetRejectReasonsAction,
    ICreateRejectReasonAction,
    IUpdateRejectReasonAction,
    IDeleteRejectReasonAction,
} from './interface';

interface IOrganizationSlice {
    companyInfo: ICompany | null;
    tags: Array<ITag>;
    companySettings: ICompanySettings | null;
    uiState: {
        isGetCompanyInfoLoading: boolean;
        isGetCompanySettingsLoading: boolean;
        isUpdateCompanySettingsLoading: boolean;
        isPostConnectGmailLoading: boolean;
        isPostConnectOutlookLoading: boolean;
        isGetTagsLoading: boolean;
        isCreateTagLoading: boolean;
        isUpdateTagLoading: boolean;
        isDeleteTagLoading: boolean;
        isDeleteCompanyMailboxLoading: boolean;
        isCreateRejectReasonLoading: boolean;
        isUpdateRejectReasonLoading: boolean;
        isDeleteRejectReasonLoading: boolean;
    };
}

const initialState: IOrganizationSlice = {
    companyInfo: null,
    tags: [],
    companySettings: null,
    uiState: {
        isGetCompanyInfoLoading: false,
        isGetCompanySettingsLoading: false,
        isUpdateCompanySettingsLoading: false,
        isPostConnectGmailLoading: false,
        isPostConnectOutlookLoading: false,
        isGetTagsLoading: false,
        isCreateTagLoading: false,
        isUpdateTagLoading: false,
        isDeleteTagLoading: false,
        isDeleteCompanyMailboxLoading: false,
        isCreateRejectReasonLoading: false,
        isUpdateRejectReasonLoading: false,
        isDeleteRejectReasonLoading: false,
    },
};

export const sliceName = 'organization';
export const organizationSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchCompanyInfo: (
            state,
            _action: PayloadAction<IFetchCompanyInfoAction>
        ) => {
            state.uiState.isGetCompanyInfoLoading = true;
        },
        setCompanyInfo: (
            state,
            action: PayloadAction<ISetCompanyInfoAction>
        ) => {
            state.companyInfo = action.payload.companyInfo;
            state.uiState.isGetCompanyInfoLoading = false;
        },
        fetchCompanySettings: (state) => {
            state.uiState.isGetCompanySettingsLoading = true;
        },
        setCompanySettings: (
            state,
            action: PayloadAction<ISetCompanySettingsAction>
        ) => {
            state.companySettings = action.payload.companySettings;
            state.uiState.isGetCompanySettingsLoading = false;
        },
        fetchCompanySettingsDone: (state) => {
            state.uiState.isGetCompanySettingsLoading = false;
        },
        updateCompanySettings: (
            state,
            _action: PayloadAction<IUpdateCompanySettingsAction>
        ) => {
            state.uiState.isUpdateCompanySettingsLoading = true;
        },
        updateCompanySettingsDone: (state) => {
            state.uiState.isUpdateCompanySettingsLoading = false;
        },
        postConnectGmail: (
            state,
            _action: PayloadAction<IPostConnectGmailAction>
        ) => {
            state.uiState.isPostConnectGmailLoading = true;
        },
        postConnectGmailDone: (state) => {
            state.uiState.isPostConnectGmailLoading = false;
        },
        postConnectOutlook: (
            state,
            _action: PayloadAction<IPostConnectOutlookAction>
        ) => {
            state.uiState.isPostConnectOutlookLoading = true;
        },
        postConnectOutlookDone: (state) => {
            state.uiState.isPostConnectOutlookLoading = false;
        },
        fetchTags: (state) => {
            state.uiState.isGetTagsLoading = true;
        },
        fetchTagsDone: (state) => {
            state.uiState.isGetTagsLoading = false;
        },
        setTags: (state, action: PayloadAction<ISetTagsAction>) => {
            state.tags = action.payload.tags;
        },
        createTag: (state, _action: PayloadAction<ICreateTagAction>) => {
            state.uiState.isCreateTagLoading = true;
        },
        createTagDone: (state) => {
            state.uiState.isCreateTagLoading = false;
        },
        updateTag: (state, _action: PayloadAction<IUpdateTagAction>) => {
            state.uiState.isUpdateTagLoading = true;
        },
        updateTagDone: (state) => {
            state.uiState.isUpdateTagLoading = false;
        },
        deleteTag: (state, _action: PayloadAction<IDeleteTagAction>) => {
            state.uiState.isDeleteTagLoading = true;
        },
        deleteTagDone: (state) => {
            state.uiState.isDeleteTagLoading = false;
        },
        deleteCompanyMailbox: (
            state,
            _action: PayloadAction<IDeleteCompanyMailboxAction>
        ) => {
            state.uiState.isDeleteCompanyMailboxLoading = true;
        },
        deleteCompanyMailboxDone: (state) => {
            state.uiState.isDeleteCompanyMailboxLoading = false;
        },
        setRejectReasons: (
            state,
            action: PayloadAction<ISetRejectReasonsAction>
        ) => {
            if (state.companySettings) {
                state.companySettings.rejectReasons =
                    action.payload.rejectReasons;
            }
        },
        createRejectReason: (
            state,
            _action: PayloadAction<ICreateRejectReasonAction>
        ) => {
            state.uiState.isCreateRejectReasonLoading = true;
        },
        createRejectReasonDone: (state) => {
            state.uiState.isCreateRejectReasonLoading = false;
        },
        updateRejectReason: (
            state,
            _action: PayloadAction<IUpdateRejectReasonAction>
        ) => {
            state.uiState.isUpdateRejectReasonLoading = true;
        },
        updateRejectReasonDone: (state) => {
            state.uiState.isUpdateRejectReasonLoading = false;
        },
        deleteRejectReason: (
            state,
            _action: PayloadAction<IDeleteRejectReasonAction>
        ) => {
            state.uiState.isDeleteRejectReasonLoading = true;
        },
        deleteRejectReasonDone: (state) => {
            state.uiState.isDeleteRejectReasonLoading = false;
        },
    },
});

export const organizationActions = organizationSlice.actions;
export default organizationSlice.reducer;
